import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../../state/useSession';
import { chatStore } from '@/storage/projectStorePort';
import type { ChatMessage } from '@/llm/types';
import type { AppDefinition } from '@/types/app';
// Pure: the order an edit — and an undo — is saved and published in, and the words for each
// outcome. The phone's, so the two clients say the same things.
import {
  UNDO_LAST_CHANGE,
  UNDONE,
  UNDONE_BUT_OFFLINE,
  runUndo,
  runWebAppEdit,
  undoConfirmation,
} from '@/webapp/chatEdit';
// Pure: the earlier versions an edit keeps. Shared with the phone.
import { lastVersion } from '@/webapp/editHistory';
import { editApp } from '../../api/build';
import { ApiError } from '../../api/client';
import {
  CHANGED_MEANWHILE,
  LIVE_UPDATED,
  SAVED_BUT_OFFLINE,
  publishLatest,
  saveSource,
  saveUndo,
  storedDefinition,
  storedForEdit,
} from '../../state/livePage';
import { useProjects } from '../../state/projects';
import { Button } from '../../ui';

/**
 * Why the live page was not updated, in the words that can help.
 *
 * The shared edit flow ends any failure it is not told about with "publish again when you have
 * a connection". That is true of exactly one kind: a request that got no answer. Everything the
 * server or this client DECIDED — a takedown, a page over 300KB, a session that expired, an app
 * with no id — is marked `refused`, so the reply is "Saved, but not live: <that sentence>" and
 * nobody is sent to check a connection that was fine. Chat edits publish themselves, so these
 * are no longer rare: every one of them now ends in this reply.
 *
 * A takedown's sentence is the body's `error`; the 403's own message says only "You do not have
 * access to that."
 */
function publishFailure(err: unknown): unknown {
  if (err instanceof ApiError && (err.kind === 'offline' || err.kind === 'timeout')) return err;
  const sentence = err instanceof ApiError && (err.code === 'taken_down' || err.takenDown === true)
    ? err.reason?.trim() || err.message
    : err instanceof Error ? err.message : '';
  return Object.assign(new Error(sentence), { refused: true as const });
}

/** `crypto.randomUUID` is not available everywhere this runs; these two always are. */
const newEditKey = () => `edit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/**
 * `**bold**`, and nothing else.
 *
 * The build's own summary named the app in bold, and printed as text it read "I built **Shot
 * Tracker Golf**". It is plain text now, but messages already stored say it that way, so this is
 * fixed where they are shown. React nodes, never HTML: a message can quote whatever somebody
 * typed.
 */
function withBold(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <Fragment key={i}>{part}</Fragment>,
  );
}

/**
 * Chat drives refinement. The instruction stays in the box until the change succeeds:
 * losing what someone typed because the model failed is the worst possible response to
 * a failure they did not cause.
 *
 * A change that succeeds goes live by itself, as it does on the phone: saved first, then
 * published as a CONTENT update — a page its owner took offline stays offline, a password page
 * stays behind its password — and the reply says which happened, or that the page was not
 * updated and why. Until now the web saved the change and said "the preview has the change",
 * and the page people actually open stayed as it was until somebody found "Publish changes".
 */
export default function ChatPanel({
  projectId,
  appId,
  goal,
  source,
  onBusyChange,
  prefill,
}: {
  projectId: string;
  /**
   * The app's own id, which its page is assembled under. Checked before an edit is paid for:
   * an app without one could be charged for a change that can never reach its page.
   */
  appId: string;
  /**
   * Not read any more: an edit no longer assembles the page here — the publish after it does,
   * under the name as stored. Optional so the editor can stop passing it.
   */
  appName?: string;
  goal: string;
  source: string;
  /** Told when an edit starts and ends, so nothing else publishes while it is on its way. */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Words another part of the editor asks to have said here — "Add sign-in", say — put in the
   * box for the owner to read and send, never sent for them: a message costs credits and changes
   * their app. A new `nonce` is a new request, so asking for the same words twice still works.
   * The phone's ChatView takes the same thing.
   */
  prefill?: { text: string; nonce: number } | null;
}) {
  const { user } = useSession();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEnd = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const undoDialog = useRef<HTMLDialogElement>(null);
  /** What Undo asks first, worked out from the record as stored when it was pressed. */
  const [undoAsk, setUndoAsk] = useState<{ title: string; body: string; confirm: string } | null>(null);
  /*
   * The request key of the change on its way, or of one whose answer never came. An edit is
   * charged before it runs, so a resend of the SAME words for the SAME source carries the SAME
   * key and the server can hand back what the first attempt made rather than charge again — the
   * phone's rule (`chatStore`). Anything else is a new change; and once one is applied it is
   * forgotten, so the same words sent after an undo are made again, not replayed.
   */
  const pendingEdit = useRef<{ key: string; source: string; instruction: string } | null>(null);
  /*
   * The version before the last change, from the record as this screen has it. `updateProject`
   * puts every write here — an edit's save, an undo — so the button follows the history rather
   * than being remembered separately and going stale.
   */
  const opened = useProjects((s) => (s.currentProject?.id === projectId ? s.currentProject.definition : null));
  const previous = opened ? lastVersion(opened as AppDefinition) : null;

  // A new request replaces whatever was in the box, once — keyed on the nonce alone, since the
  // text of the same request is the same request.
  const prefillNonce = prefill?.nonce;
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    box.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillNonce]);

  useEffect(() => {
    void chatStore()
      .getByProjectId(projectId)
      .then(setMessages)
      .catch(() => setMessages([]));
  }, [projectId]);

  useEffect(() => {
    listEnd.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  async function send(): Promise<void> {
    const instruction = text.trim();
    if (!instruction || busy) return;
    setError(null);
    setBusy(true);
    onBusyChange?.(true);
    const ownerId = user?.id ?? '';
    // The screen's copy is read when the project opens; this puts the stored one on screen.
    const refreshScreen = () => void useProjects.getState().loadProject(projectId, ownerId);
    const now = new Date().toISOString();
    try {
      /*
       * What the model is asked to change: the app as STORED, read before anything is paid for.
       * The `source` this screen was drawn with is only re-read when the project is opened, so
       * after another tab changed the app it was older than the store, and every send — the
       * resend CHANGED_MEANWHILE asks for included — paid for an edit the save then refused.
       */
      const current = await storedForEdit(projectId, ownerId);
      const sent = current.source;
      if (sent !== source) refreshScreen();
      const userMessage = await chatStore().addMessage(projectId, {
        role: 'user',
        content: instruction,
        timestamp: now,
      });
      setMessages((m) => [...m, userMessage]);

      const outcome = await runWebAppEdit({
        source: sent,
        instruction,
        // The phone's goal: the project's description, else the app's own.
        goal: current.goal ?? (goal || undefined),
        /*
         * /edit alone; the publish below assembles the saved source under the app's EXISTING
         * id. The plan goes with it, as it does from the phone: it is how the edit keeps the
         * rules, the limits and the collections the build set.
         */
        edit: async (input) => {
          const pending = pendingEdit.current;
          const requestKey = pending && pending.source === sent && pending.instruction === instruction
            ? pending.key
            : newEditKey();
          pendingEdit.current = { key: requestKey, source: sent, instruction };
          const result = await editApp({ appId, ...input, plan: current.plan, requestKey });
          // Only the code and the drift report are read; the counters are the server's own.
          return { appCode: result.appCode, attempts: 0, gateFailures: 0, ...(result.drift ? { drift: result.drift } : {}) };
        },
        // The version it replaces is kept with it, so Undo last change can bring it back.
        save: (next) =>
          saveSource(projectId, ownerId, sent, next, instruction).catch((err: unknown) => {
            // Refused because the stored app moved on: show that version, the one a resend edits.
            refreshScreen();
            throw err;
          }),
        publish: async () => {
          const published = await publishLatest(projectId, ownerId).catch((err: unknown) => {
            throw publishFailure(err);
          });
          return { offline: !published.webPublished };
        },
      });

      if (outcome.status === 'failed') {
        // Nothing changed, so what they typed stays in the box.
        setError(outcome.message);
        return;
      }
      // Applied: the same words sent again are a new change, not this one replayed.
      pendingEdit.current = null;

      const said = outcome.status === 'saved_not_live'
        ? outcome.message
        : [outcome.offline ? SAVED_BUT_OFFLINE : LIVE_UPDATED, outcome.note].filter(Boolean).join('\n\n');
      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: said,
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
      /*
       * Saved either way, so the instruction has been used — but only IT is cleared. The box
       * stays usable while an edit is on its way, and words typed or put there meanwhile are
       * the next request, not this one.
       */
      setText((t) => (t.trim() === instruction ? '' : t));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That change could not be made.');
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }

  /**
   * Undo last change, asked first — the phone's question, in its words (`undoConfirmation`): it
   * acts at once on a page other people may be using, and the change it throws away may have
   * been paid for. There is no redo. Worked out from the record as STORED, so it names the change
   * that will actually be undone even if another tab made one since this screen was drawn.
   */
  async function askUndo(): Promise<void> {
    if (busy) return;
    setError(null);
    try {
      const ask = undoConfirmation(await storedDefinition(projectId, user?.id ?? ''));
      if (!ask) {
        // Undone elsewhere meanwhile: show what is stored, where there is nothing to take back.
        void useProjects.getState().loadProject(projectId, user?.id ?? '');
        return;
      }
      setUndoAsk(ask);
      undoDialog.current?.showModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be undone.');
    }
  }

  function closeUndo(): void {
    undoDialog.current?.close();
    setUndoAsk(null);
  }

  /**
   * Put back the version the last change replaced, and put THAT on the page — the phone's
   * `runUndo`: saved first, then published as a content update (a page taken offline stays
   * offline, a password page keeps its password), and said in the chat in the same words.
   * Busy like an edit, so nothing else publishes meanwhile. Only the version is restored, never
   * the chat: what was asked stays in the conversation, one Send away.
   */
  async function undo(): Promise<void> {
    closeUndo();
    if (busy) return;
    setError(null);
    setBusy(true);
    setUndoing(true);
    onBusyChange?.(true);
    const ownerId = user?.id ?? '';
    const refreshScreen = () => void useProjects.getState().loadProject(projectId, ownerId);
    try {
      const from = await storedDefinition(projectId, ownerId);
      const outcome = await runUndo({
        definition: from,
        save: (restored) =>
          saveUndo(projectId, ownerId, from.source ?? '', restored.source ?? '').catch((err: unknown) => {
            // Moved on meanwhile: show the version as stored, the one a second Undo would act on.
            if (err instanceof Error && err.message === CHANGED_MEANWHILE) refreshScreen();
            throw err;
          }),
        publish: async () => {
          const published = await publishLatest(projectId, ownerId).catch((err: unknown) => {
            throw publishFailure(err);
          });
          return { offline: !published.webPublished };
        },
      });
      if (outcome.status === 'failed') {
        setError(outcome.message);
        return;
      }
      const said = outcome.status === 'saved_not_live'
        ? outcome.message
        : [outcome.offline ? UNDONE_BUT_OFFLINE : UNDONE, outcome.note].filter(Boolean).join('\n\n');
      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: said,
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be undone.');
    } finally {
      setBusy(false);
      setUndoing(false);
      onBusyChange?.(false);
    }
  }

  return (
    <div className="chat-panel">
      <ol className="chat-list">
        {messages.map((m) => (
          <li key={m.id} className={`chat-msg chat-${m.role}`}>
            <span className="type-body">{withBold(m.content)}</span>
          </li>
        ))}
        <div ref={listEnd} />
      </ol>

      {/* Only when there is a version to go back to: a button that does nothing is a question. */}
      {previous && (
        <div className="build-actions">
          <Button
            title={UNDO_LAST_CHANGE}
            variant="ghost"
            loading={undoing}
            disabled={busy}
            onPress={() => void askUndo()}
          />
        </div>
      )}

      <dialog
        ref={undoDialog}
        aria-label={undoAsk?.title ?? UNDO_LAST_CHANGE}
        className="confirm-dialog"
        onClose={() => setUndoAsk(null)}
      >
        {undoAsk && (
          <>
            <h2 className="type-title">{undoAsk.title}</h2>
            {undoAsk.body.split('\n\n').map((para) => (
              <p key={para} className="type-body">{para}</p>
            ))}
            <div className="build-actions">
              <Button title={undoAsk.confirm} variant="dangerSoft" onPress={() => void undo()} />
              <Button title="Cancel" variant="ghost" onPress={closeUndo} />
            </div>
          </>
        )}
      </dialog>

      <div className="chat-composer">
        <label className="type-caption field-label" htmlFor="instruction">
          What should I change?
        </label>
        <textarea
          ref={box}
          id="instruction"
          className="field"
          rows={2}
          // The app exists: this box changes it, and never describes a new one.
          placeholder="Describe a change…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        <Button title="Send" onPress={() => void send()} loading={busy && !undoing} disabled={!text.trim() || undoing} />
      </div>
    </div>
  );
}
